import { useEffect } from "react";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudIcon,
  PencilIcon,
} from "lucide-react";

import { Column, KeyboardShortcut, Row } from "@/components";
import { Button } from "@/components/ui/button";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

export const IntakeHeader = <T extends string>({
  activeTab,
  isEditing,
  screenName,
  setActiveTab,
  setIsEditing,
  tabs,
  tabToLabel,
}: {
  activeTab: T;
  isEditing: boolean;
  screenName: MIXPANEL_SCREENS;
  setActiveTab: (tab: T) => void;
  setIsEditing: (isEditing: boolean) => void;
  tabs: T[];
  tabToLabel: Record<T, string>;
}) => {
  const currentIndex = tabs.indexOf(activeTab);
  const currentStep = currentIndex + 1;
  const totalSteps = tabs.length;

  const previousTab = currentIndex > 0 ? tabs[currentIndex - 1] : undefined;
  const nextTab =
    currentIndex < tabs.length - 1 ? tabs[currentIndex + 1] : undefined;

  const label = tabToLabel[activeTab];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isEditing && e.key === "e") {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed("E (Edit)", screenName);
        setIsEditing(true);
      } else if (isEditing && e.key === "Escape") {
        // Blur active input/textarea first so save-on-blur fires
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        mixpanelUtils.trackKeyboardShortcutUsed("Esc (Complete)", screenName);
        setIsEditing(false);
      }

      if (previousTab && e.metaKey && e.shiftKey && e.key === ",") {
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Shift+< (Previous)",
          screenName,
        );
        setActiveTab(previousTab);
      } else if (nextTab && e.metaKey && e.shiftKey && e.key === ".") {
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Shift+> (Next)",
          screenName,
        );
        setActiveTab(nextTab);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previousTab, nextTab, isEditing, screenName, setIsEditing, setActiveTab]);

  return (
    <Column className="sticky top-0 z-20 gap-4 bg-white pb-4">
      {/* Navigation bar */}
      <Row className="items-center border-b border-slate-200 pb-4">
        <Row className="max-w-3xl flex-1 items-center justify-between">
          {/* Previous button */}
          <Row className="w-[180px]">
            {previousTab ? (
              <Button
                className="gap-1.5"
                onClick={() => {
                  mixpanelUtils.trackButtonClick("Previous", screenName);
                  setActiveTab(previousTab);
                }}
                variant="outlineSecondary"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Previous
              </Button>
            ) : (
              <div />
            )}
          </Row>

          {/* Progress dots */}
          <Row className="items-center gap-1.5">
            {tabs.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "h-2.5 rounded-full transition-all",
                  i + 1 === currentStep
                    ? "w-6 bg-primary-700"
                    : i + 1 < currentStep
                      ? "w-2.5 bg-primary-700"
                      : "w-2.5 bg-slate-200",
                )}
              />
            ))}
          </Row>

          {/* Complete/Edit + Next */}
          <Row className="items-center gap-4">
            <Button
              className="w-[120px] gap-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-600"
              onClick={() => {
                if (
                  isEditing &&
                  document.activeElement instanceof HTMLElement
                ) {
                  document.activeElement.blur();
                }
                mixpanelUtils.trackButtonClick(
                  isEditing ? "Complete" : "Edit",
                  screenName,
                );
                setIsEditing(!isEditing);
              }}
              variant="ghost"
            >
              {isEditing ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Complete
                  <KeyboardShortcut text="Esc" variant="blue" />
                </>
              ) : (
                <>
                  <PencilIcon className="h-4 w-4" />
                  Edit
                  <KeyboardShortcut text="E" variant="blue" />
                </>
              )}
            </Button>
            {nextTab ? (
              <Button
                className="gap-1.5"
                onClick={() => {
                  mixpanelUtils.trackButtonClick("Next", screenName);
                  setActiveTab(nextTab);
                }}
                variant="secondaryPrimary"
              >
                Next
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            ) : (
              <div className="w-[100px]" />
            )}
          </Row>
        </Row>

        {/* Autosave indicator */}
        <Row className="ml-auto items-center gap-2 pl-6 text-sm text-slate-500">
          <CloudIcon className="h-4 w-4" />
          All changes saved
        </Row>
      </Row>

      {/* Step info + title */}
      <Column className="max-w-3xl">
        <p className="text-sm text-slate-500">
          Step {currentStep} of {totalSteps}
        </p>
        <h2 className="text-2xl font-normal text-black">{label}</h2>
      </Column>
    </Column>
  );
};
